import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import AppShell from "../components/AppShell";
import { FUNDER_NAV } from "./FunderTerminal";
import { Lock, FileText, Download, ArrowLeft, ShieldCheck, Star, MailOpen, Eye, X, Sparkles } from "lucide-react";
import { sdgListLabels } from "../lib/sdg";

export default function FunderSMEDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [messages, setMessages] = useState([]);
  const [preview, setPreview] = useState(null); // { url, filename, contentType }

  useEffect(() => () => { if (preview?.url) URL.revokeObjectURL(preview.url); }, [preview]);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/funder/sme/${id}`);
      setData(r.data);
    } catch (err) {
      if (process.env.NODE_ENV === "development") console.warn("[FunderSMEDetail] load failed", err);
    }
    try { const s = await api.get("/funder/saved-ids"); setSaved((s.data.ids||[]).includes(id)); }
    catch (err) { if (process.env.NODE_ENV === "development") console.warn("[FunderSMEDetail] saved-ids failed", err); }
    try { const m = await api.get(`/funder/messages/from-company/${id}`); setMessages(m.data); }
    catch (err) { if (process.env.NODE_ENV === "development") console.warn("[FunderSMEDetail] messages failed", err); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const toggleSave = async () => {
    if (saved) { await api.delete(`/funder/sme/${id}/save`); setSaved(false); }
    else { await api.post(`/funder/sme/${id}/save`); setSaved(true); }
  };

  const requestAccess = async () => {
    setBusy(true);
    try { await api.post(`/funder/sme/${id}/request-access`); await load(); }
    finally { setBusy(false); }
  };

  const downloadDoc = async (doc_id, filename) => {
    const r = await api.get(`/documents/${doc_id}/download`, { responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  };

  const previewDoc = async (doc_id, filename, contentType) => {
    if (preview?.url) URL.revokeObjectURL(preview.url);
    const r = await api.get(`/documents/${doc_id}/download`, { responseType: "blob" });
    const blob = new Blob([r.data], { type: contentType || "application/pdf" });
    const url = URL.createObjectURL(blob);
    setPreview({ url, filename, contentType: contentType || "application/pdf" });
  };

  const isPreviewable = (d) => {
    const ct = (d.content_type || "").toLowerCase();
    const fn = (d.filename || "").toLowerCase();
    return ct.startsWith("application/pdf") || ct.startsWith("image/") || fn.endsWith(".pdf");
  };

  if (!data) return <AppShell nav={FUNDER_NAV}><p className="text-sm text-neutral-500">Loading…</p></AppShell>;
  const s = data.sme;
  const viewerIsAdmin = !!data.viewer_is_admin;

  return (
    <AppShell nav={FUNDER_NAV}>
      <button onClick={()=>nav(viewerIsAdmin ? `/admin/businesses/${id}` : "/funder/terminal")} data-testid="back-to-terminal" className="inline-flex items-center gap-2 text-sm font-semibold text-neutral-600 hover:text-neutral-900">
        <ArrowLeft className="h-4 w-4"/> {viewerIsAdmin ? "Back to admin view" : "Back to terminal"}
      </button>
      {viewerIsAdmin && (
        <div className="mt-3 rounded-lg border border-emerald-300 bg-emerald-50 px-4 py-2.5 text-sm text-emerald-900" data-testid="sme-admin-view-banner">
          <b>Admin view</b> · You are inspecting this business through the funder lens. Data room access is granted automatically — no founder approval needed.
        </div>
      )}
      <div className="mt-4 flex flex-wrap items-baseline justify-between gap-3">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-3xl font-bold text-neutral-900" data-testid="sme-name">{s.company_name}</h1>
            {s.kind === "project" && (
              <span data-testid="sme-project-badge"
                className="rounded bg-amber-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-amber-700">Project</span>
            )}
            {!viewerIsAdmin && (
              <button data-testid="sme-save-btn" onClick={toggleSave}
                className={"inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-semibold transition-colors " +
                  (saved ? "border-[#0A8B85] bg-[#0A8B85] text-white" : "border-neutral-200 bg-white text-neutral-700 hover:bg-neutral-50")}>
                <Star className={"h-3.5 w-3.5 " + (saved ? "fill-current" : "")}/>
                {saved ? "Saved" : "Save deal"}
              </button>
            )}
          </div>
          <p className="mt-1 text-sm text-neutral-600">
            {s.kind === "project"
              ? [s.project_location, s.sector, s.project_stage_label].filter(Boolean).join(" · ")
              : `${s.country} · ${s.sector} · ${s.stage}`}
          </p>
        </div>
        <div className="flex items-center gap-5">
          <div className="text-right">
            <p className="text-5xl font-extrabold text-[#0A8B85]" data-testid="sme-compat">
              {viewerIsAdmin ? "—" : (s.compatibility ?? 0)}
            </p>
            <p className="text-xs uppercase tracking-wider text-neutral-500">Mandate Fit</p>
          </div>
          <div className="text-right">
            <p className="text-2xl font-bold text-neutral-700" data-testid="sme-readiness">{s.readiness_score || 0}</p>
            <p className="text-xs uppercase tracking-wider text-neutral-400">Readiness</p>
          </div>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-5 lg:grid-cols-3">
        {messages.length > 0 && (
          <div className="rounded-2xl border border-[#0A8B85]/30 bg-[#0A8B85]/[0.04] p-6 lg:col-span-3" data-testid="sme-messages">
            <div className="flex items-center gap-2">
              <MailOpen className="h-4 w-4 text-[#0A8B85]"/>
              <p className="text-xs font-bold uppercase tracking-wider text-[#0A8B85]">Direct message from founder</p>
            </div>
            {messages.map(m => (
              <div key={m.message_id} className="mt-4 rounded-xl bg-white p-5" data-testid={`message-${m.message_id}`}>
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <p className="font-bold text-neutral-900">{m.subject}</p>
                  <span className="text-xs text-neutral-500">{new Date(m.created_at).toLocaleString()}</span>
                </div>
                <p className="mt-1 text-xs text-neutral-500">From: <b className="text-neutral-700">{m.from_name}</b></p>
                <p className="mt-3 whitespace-pre-wrap text-sm leading-relaxed text-neutral-800">{m.message}</p>
              </div>
            ))}
          </div>
        )}

        <div className="rounded-2xl border border-neutral-200 bg-white p-6 lg:col-span-2">
          <p className="text-xs font-bold uppercase tracking-wider text-neutral-500">Value proposition</p>
          <p className="mt-2 text-sm text-neutral-800">{s.value_prop}</p>
          <div className="mt-5 grid grid-cols-2 gap-4 text-sm md:grid-cols-4">
            {s.kind === "project" ? (
              <>
                <Stat label="Raising" value={`$${((s.ticket_min||0)/1000).toFixed(0)}K`}/>
                <Stat label="Capital sought" value={s.project_capital_type || "—"}/>
                <Stat label="Stage" value={s.project_stage_label || "—"}/>
                <Stat label="Category" value={s.sector || "—"}/>
              </>
            ) : (
              <>
                <Stat label="Ticket" value={`$${((s.ticket_min||0)/1000).toFixed(0)}K–$${((s.ticket_max||0)/1000).toFixed(0)}K`}/>
                <Stat label="Revenue" value={`$${((s.revenue_annual||0)/1000).toFixed(0)}K`}/>
                <Stat label="Employees" value={s.employees || "—"}/>
                <Stat label="Impact" value={sdgListLabels(s.sdgs)}/>
              </>
            )}
          </div>
        </div>

        {s.kind === "project" ? <ProjectAnalysisPanel sme={s} /> : <AIAnalysisPanel sme={s} />}

        <div className="rounded-2xl border border-neutral-200 bg-white p-6" data-testid="vdr-panel">
          <div className="flex items-center gap-2">
            {data.vdr_access ? <ShieldCheck className="h-5 w-5 text-[#0A8B85]"/> : <Lock className="h-5 w-5 text-neutral-400"/>}
            <p className="font-bold text-neutral-900">Virtual Data Room</p>
          </div>
          {data.vdr_access ? (
            <>
              <p className="mt-2 text-xs text-[#0A8B85]">NDA accepted · access audit-logged</p>
              <ul className="mt-4 space-y-2">
                {data.documents.length === 0 && <li className="text-sm text-neutral-500">No documents.</li>}
                {data.documents.map(d => (
                  <li key={d.doc_id} className="flex items-center gap-2 rounded-lg border border-neutral-200 px-3 py-2 text-sm">
                    <FileText className="h-4 w-4 text-[#0A8B85]"/>
                    <span className="flex-1 truncate" title={d.filename}>{d.filename}</span>
                    {isPreviewable(d) && (
                      <button data-testid={`preview-${d.doc_id}`} onClick={()=>previewDoc(d.doc_id, d.filename, d.content_type)}
                        title="Preview"
                        className="text-neutral-500 hover:text-[#0A8B85]"><Eye className="h-4 w-4"/></button>
                    )}
                    <button data-testid={`download-${d.doc_id}`} onClick={()=>downloadDoc(d.doc_id, d.filename)}
                      title="Download"
                      className="text-[#0A8B85] hover:text-[#066667]"><Download className="h-4 w-4"/></button>
                  </li>
                ))}
              </ul>
            </>
          ) : (
            <>
              <p className="mt-2 text-xs text-neutral-500">Request NDA-gated access to view documents.</p>
              <button data-testid="request-vdr" disabled={busy} onClick={requestAccess}
                className="mt-4 w-full rounded-lg bg-[#0A8B85] py-2.5 text-sm font-semibold text-white hover:bg-[#066667] disabled:opacity-50">
                {busy ? "Requesting…" : "Accept NDA & Open VDR"}
              </button>
            </>
          )}
        </div>
      </div>

      {preview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" data-testid="vdr-preview-modal">
          <div className="flex h-[90vh] w-full max-w-5xl flex-col rounded-2xl border border-neutral-800 bg-neutral-950 shadow-2xl">
            <div className="flex items-center justify-between border-b border-neutral-800 px-5 py-3">
              <div className="flex items-center gap-2 min-w-0">
                <ShieldCheck className="h-4 w-4 flex-shrink-0 text-[#0A8B85]"/>
                <p className="truncate text-sm font-semibold text-neutral-100" data-testid="preview-filename">{preview.filename}</p>
              </div>
              <div className="flex items-center gap-2">
                <a href={preview.url} download={preview.filename}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-neutral-700 px-3 py-1.5 text-xs font-semibold text-neutral-200 hover:bg-neutral-800"
                  data-testid="preview-download">
                  <Download className="h-3.5 w-3.5"/> Download
                </a>
                <button onClick={() => { URL.revokeObjectURL(preview.url); setPreview(null); }}
                  data-testid="preview-close"
                  className="rounded-lg p-1.5 text-neutral-400 hover:bg-neutral-800 hover:text-neutral-100">
                  <X className="h-4 w-4"/>
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-hidden bg-neutral-900">
              {preview.contentType.startsWith("image/") ? (
                <div className="flex h-full items-center justify-center overflow-auto p-4">
                  <img src={preview.url} alt={preview.filename} className="max-h-full max-w-full object-contain"/>
                </div>
              ) : (
                <iframe src={preview.url} title={preview.filename} className="h-full w-full" data-testid="preview-iframe"/>
              )}
            </div>
            <p className="border-t border-neutral-800 px-5 py-2 text-[10px] uppercase tracking-wider text-neutral-500">
              This view is audit-logged · NDA-bound · do not redistribute
            </p>
          </div>
        </div>
      )}
    </AppShell>
  );
}
const Stat = ({ label, value }) => (
  <div><p className="text-[10px] font-bold uppercase tracking-wider text-neutral-500">{label}</p><p className="mt-0.5 font-semibold text-neutral-900">{value}</p></div>
);

function ProjectAnalysisPanel({ sme }) {
  const prs = sme?.prs_analysis || {};
  const unlocked = !!sme?.readiness_report_unlocked;
  const verified = sme?.verified_readiness_status === "verified";
  const pillars = prs.pillar_scores || {};
  const verdicts = prs.verdicts || {};
  const routeLabel = { debt_bank: "Bank Debt", debt_dfi: "DFI Debt", equity: "Equity", grant: "Grant / Blended" };
  const verdictCls = {
    viable: "bg-emerald-50 border-emerald-200 text-emerald-700",
    possible: "bg-amber-50 border-amber-200 text-amber-700",
    not_yet: "bg-rose-50 border-rose-200 text-rose-700",
  };
  const downloadReport = async () => {
    try {
      const { api } = await import("../lib/api");
      const resp = await api.get(`/funder/sme/${sme.company_id}/report.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(sme.company_name || "project").replace(/\s+/g, "-").toLowerCase()}-prr-report.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      alert(e?.response?.data?.detail || e?.message || "Unable to download report");
    }
  };

  if (prs.readiness_score == null) {
    return (
      <div className="rounded-2xl border border-neutral-200 bg-white p-6 lg:col-span-3" data-testid="prs-panel-empty">
        <p className="text-xs font-bold uppercase tracking-wider text-neutral-500">Project Readiness</p>
        <p className="mt-3 text-sm text-neutral-500">This project hasn&apos;t been scored yet.</p>
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-neutral-200 bg-white p-6 lg:col-span-3"
         data-testid={unlocked ? "prs-panel-unlocked" : "prs-panel-locked"}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-amber-600"/>
          <p className="text-xs font-bold uppercase tracking-wider text-neutral-500">
            Project Readiness Report · PRS v1.0
          </p>
        </div>
        <div className="flex items-center gap-2">
          {verified && (
            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-emerald-700" data-testid="prs-panel-verified-chip">
              <ShieldCheck className="h-3 w-3"/> Verified · {sme.verified_readiness_score}/100
            </span>
          )}
          {unlocked ? (
            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-emerald-700">
              <ShieldCheck className="h-3 w-3"/> NDA / unlocked
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-amber-700">
              <Lock className="h-3 w-3"/> Summary only
            </span>
          )}
          {unlocked && (
            <button onClick={downloadReport} data-testid="prr-report-download-pdf"
              className="inline-flex items-center gap-1 rounded-lg bg-[#0A8B85] px-3 py-1.5 text-xs font-bold text-white hover:bg-[#066667]">
              <Download className="h-3 w-3"/> Download PDF
            </button>
          )}
        </div>
      </div>

      <div className="mt-4 flex flex-wrap items-baseline gap-3">
        <span className="text-4xl font-extrabold text-[#0A8B85]" data-testid="prs-panel-score">{prs.readiness_score}</span>
        <span className="text-xs uppercase tracking-wider text-neutral-500">/ 100 Project Readiness Score</span>
        {prs.band_label && (
          <span className="rounded-full bg-neutral-100 px-2.5 py-0.5 text-[11px] font-bold text-neutral-700">{prs.band_label}</span>
        )}
        {prs.stage_label && (
          <span className="text-xs text-neutral-500">Stage: {prs.stage_label}</span>
        )}
      </div>
      <p className="mt-3 text-sm text-neutral-800" data-testid="prs-panel-summary">
        {prs.summary || "No summary available."}
      </p>

      {unlocked ? (
        <>
          {Object.keys(pillars).length > 0 && (
            <div className="mt-5" data-testid="prs-panel-pillars">
              <p className="text-[10px] font-bold uppercase tracking-wider text-neutral-500">Six-pillar breakdown</p>
              <div className="mt-2 grid gap-2 sm:grid-cols-2">
                {Object.entries(pillars).map(([k, p]) => {
                  const v = Math.max(0, Math.min(100, Number(p.score || 0)));
                  return (
                    <div key={k} className="rounded-lg border border-neutral-200 px-3 py-2">
                      <div className="flex items-baseline justify-between">
                        <span className="text-xs font-semibold text-neutral-800">{k.replace("PR", "PR-")} · {p.label}</span>
                        <span className="text-xs font-extrabold text-[#0A8B85]">{v}</span>
                      </div>
                      <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-neutral-100">
                        <div className="h-full bg-[#0A8B85] transition-all" style={{ width: `${v}%` }}/>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {Object.keys(verdicts).length > 0 && (
            <div className="mt-5" data-testid="prs-panel-verdicts">
              <p className="text-[10px] font-bold uppercase tracking-wider text-neutral-500">Capital route fit</p>
              <div className="mt-2 flex flex-wrap gap-2">
                {Object.entries(verdicts).map(([ft, v]) => (
                  <span key={ft}
                    className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-bold ${verdictCls[v.verdict] || verdictCls.not_yet}`}>
                    {routeLabel[ft] || ft} · {v.score} · {v.verdict === "viable" ? "Viable" : v.verdict === "possible" ? "Possible" : "Not yet"}
                  </span>
                ))}
              </div>
            </div>
          )}

          <div className="mt-5 grid gap-5 md:grid-cols-2">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wider text-neutral-500">Strengths</p>
              <ul className="mt-2 space-y-1.5" data-testid="prs-panel-strengths">
                {(prs.strengths || []).length === 0 ? <li className="text-xs text-neutral-500">—</li>
                  : (prs.strengths || []).map((st, i) => (
                    <li key={`s-${i}`} className="flex gap-2 text-sm"><span className="text-[#0A8B85]">✓</span>{st}</li>
                  ))}
              </ul>
            </div>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wider text-neutral-500">Risks &amp; gaps</p>
              <ul className="mt-2 space-y-1.5" data-testid="prs-panel-gaps">
                {(prs.gaps || []).length === 0 ? <li className="text-xs text-neutral-500">—</li>
                  : (prs.gaps || []).map((g, i) => (
                    <li key={`g-${i}`} className="flex gap-2 text-sm">
                      <span className="text-amber-600">!</span>{g.gap || g}
                    </li>
                  ))}
              </ul>
            </div>
          </div>

          {prs.analyzed_at && (
            <p className="mt-5 border-t border-neutral-100 pt-3 text-[10px] text-neutral-500">
              Last analysed {new Date(prs.analyzed_at).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })}
              {verified ? " · Independently verified by Clinq" : ""}
            </p>
          )}
        </>
      ) : (
        <div className="mt-5 flex gap-3 rounded-lg border border-amber-200 bg-amber-50/60 p-3" data-testid="prs-panel-locked-cta">
          <Lock className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-700"/>
          <p className="text-xs text-amber-900">
            The full Project Readiness Report — pillar scores, hard checks, capital-route
            verdicts and the PDF — unlocks once you accept the NDA (request VDR access below)
            or the sponsor publishes their report publicly.
          </p>
        </div>
      )}
    </div>
  );
}


function AIAnalysisPanel({ sme }) {
  const ai = sme?.ai_analysis || {};
  const unlocked = !!sme?.readiness_report_unlocked;
  const hasAnyAnalysis = ai.summary || ai.readiness_score != null;
  const pillarScores = ai.pillar_scores || {};
  const pillarLabels = {
    P1: "Strategy & Market",
    P2: "Team & Governance",
    P3: "Financial Discipline",
    P4: "Traction & Unit Economics",
    P5: "Capital & Use of Funds",
    P6: "Risk & Compliance",
  };
  const verified = sme?.verified_readiness_status === "verified";
  const downloadReport = async () => {
    try {
      const { api } = await import("../lib/api");
      const resp = await api.get(`/funder/sme/${sme.company_id}/report.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(sme.company_name || "ccr-report").replace(/\s+/g, "-").toLowerCase()}-ccr-report.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Unable to download report";
      alert(msg);
    }
  };

  if (!hasAnyAnalysis) {
    return (
      <div className="rounded-2xl border border-neutral-200 bg-white p-6 lg:col-span-3" data-testid="ai-panel-empty">
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-neutral-400"/>
          <p className="text-xs font-bold uppercase tracking-wider text-neutral-500">AI analysis</p>
        </div>
        <p className="mt-3 text-sm text-neutral-500">
          No AI analysis available — this founder hasn&apos;t run the assessment yet.
        </p>
      </div>
    );
  }

  return (
    <div
      className="rounded-2xl border border-neutral-200 bg-white p-6 lg:col-span-3"
      data-testid={unlocked ? "ai-panel-unlocked" : "ai-panel-locked"}
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-[#0A8B85]"/>
          <p className="text-xs font-bold uppercase tracking-wider text-neutral-500">
            Most recent Capital Readiness Report · Claude Opus 4.5
          </p>
        </div>
        <div className="flex items-center gap-2">
          {verified && (
            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-emerald-700">
              <ShieldCheck className="h-3 w-3"/> Verified by Clinq
            </span>
          )}
          {unlocked ? (
            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-emerald-700">
              <ShieldCheck className="h-3 w-3"/> NDA / unlocked
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-amber-700">
              <Lock className="h-3 w-3"/> Summary only
            </span>
          )}
          {unlocked && (
            <button
              onClick={downloadReport}
              data-testid="ccr-report-download-pdf"
              className="inline-flex items-center gap-1 rounded-lg bg-[#0A8B85] px-3 py-1.5 text-xs font-bold text-white hover:bg-[#066667]"
              title="Download the most recent Capital Readiness Report (PDF)"
            >
              <Download className="h-3 w-3"/> Download PDF
            </button>
          )}
        </div>
      </div>

      <div className="mt-4 flex items-baseline gap-3">
        <span className="text-4xl font-extrabold text-[#0A8B85]" data-testid="ccr-report-score">{ai.readiness_score ?? "—"}</span>
        <span className="text-xs uppercase tracking-wider text-neutral-500">/ 100 CCR Score™</span>
      </div>

      <p className="mt-3 text-sm text-neutral-800" data-testid="ai-panel-summary">
        {ai.summary || "No summary available."}
      </p>

      {unlocked ? (
        <>
          {/* Pillar scores breakdown — the canonical 6-pillar Capital Readiness Score view */}
          {Object.keys(pillarScores).length > 0 && (
            <div className="mt-5" data-testid="ccr-report-pillars">
              <p className="text-[10px] font-bold uppercase tracking-wider text-neutral-500">Pillar breakdown</p>
              <div className="mt-2 grid gap-2 sm:grid-cols-2">
                {Object.entries(pillarLabels).map(([k, label]) => {
                  const v = Math.max(0, Math.min(100, Number(pillarScores[k] || 0)));
                  return (
                    <div key={k} className="rounded-lg border border-neutral-200 px-3 py-2"
                         data-testid={`ccr-pillar-${k}`}>
                      <div className="flex items-baseline justify-between">
                        <span className="text-xs font-semibold text-neutral-800">{label}</span>
                        <span className="text-xs font-extrabold text-[#0A8B85]">{v}</span>
                      </div>
                      <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-neutral-100">
                        <div className="h-full bg-[#0A8B85] transition-all" style={{ width: `${v}%` }}/>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div className="mt-5 grid gap-5 md:grid-cols-2">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wider text-neutral-500">Strengths</p>
              <ul className="mt-2 space-y-1.5" data-testid="ai-panel-strengths">
                {(ai.strengths || []).length === 0 ? (
                  <li className="text-xs text-neutral-500">—</li>
                ) : (ai.strengths || []).map((s, i) => (
                  <li key={`s-${i}-${(s || "").slice(0, 24)}`} className="flex gap-2 text-sm"><span className="text-[#0A8B85]">✓</span>{s}</li>
                ))}
              </ul>
            </div>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wider text-neutral-500">Risks &amp; gaps</p>
              <ul className="mt-2 space-y-1.5" data-testid="ai-panel-weaknesses">
                {(ai.weaknesses || []).length === 0 ? (
                  <li className="text-xs text-neutral-500">—</li>
                ) : (ai.weaknesses || []).map((w, i) => (
                  <li key={`w-${i}-${(w || "").slice(0, 24)}`} className="flex gap-2 text-sm"><span className="text-amber-600">!</span>{w}</li>
                ))}
              </ul>
            </div>
          </div>

          {/* Capital types viable */}
          {Array.isArray(ai.capital_types_viable) && ai.capital_types_viable.length > 0 && (
            <div className="mt-5" data-testid="ccr-report-capital-types">
              <p className="text-[10px] font-bold uppercase tracking-wider text-neutral-500">Capital types viable</p>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {ai.capital_types_viable.map((t, i) => (
                  <span key={`${t}-${i}`} className="inline-flex items-center rounded-full border border-[#0A8B85]/30 bg-[#0A8B85]/5 px-2.5 py-0.5 text-[11px] font-semibold text-[#0A8B85]">
                    {t}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Verified readiness footer */}
          {sme?.analyzed_at && (
            <p className="mt-5 border-t border-neutral-100 pt-3 text-[10px] text-neutral-500" data-testid="ccr-report-meta">
              Last analysed {new Date(sme.analyzed_at).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })}
              {verified ? " · Independently verified by Clinq" : ""}
            </p>
          )}
        </>
      ) : (
        <div className="mt-5 flex gap-3 rounded-lg border border-amber-200 bg-amber-50/60 p-3" data-testid="ai-panel-locked-cta">
          <Lock className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-700"/>
          <p className="text-xs text-amber-900">
            The full Capital Readiness Report — including pillar scores, strengths, gaps and the
            PDF download — unlocks for you once you accept the NDA (request VDR access below)
            or the founder publishes their report publicly.
          </p>
        </div>
      )}
    </div>
  );
}

